import { FlakinessReport } from '@flakiness/flakiness-report';
import { outcomeToStatusFilter } from '@flakiness/server/common/fql/extractor.js';
import { Matcher } from '@flakiness/server/common/fql/matcher.js';
import { Query } from '@flakiness/server/common/fql/query.js';
import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { Stats } from '@flakiness/server/common/stats/stats.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { wireOutcomesToOutcome, WireTypes } from '@flakiness/server/common/wireTypes.js';
import { CHECK } from '@flakiness/shared/common/utils.js';
import { ContextType } from '@lit/context';
import { Task, TaskStatus } from '@lit/task';
import { SlDialog } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { keyed } from 'lit/directives/keyed.js';
import { Temporal } from 'temporal-polyfill';
import { api } from '../api.js';
import { linkStyles } from '../components/cssstyles.js';
import { FKHeadSelectEvent } from '../components/head-selector.js';
import { FKOutcomeSelectEvent, OUTCOME_ORDER } from '../components/outcome-legend.js';
import { createTestHealthCalendarState, TestHealthCalendarLinkifier } from '../components/test-health-calendar.js';
import { consume, contexts } from '../contexts.js';
import { TestHistorySnippetSelection } from '../data-table-providers/genericColumns.js';
import { handleLink, Linkifier } from '../linkifier.js';
import { PageCommit } from '../page-commit.js';
import { PageProjectTests } from '../page-project-tests.js';
import { PageRun } from '../page-run.js';
import { tasks } from '../tasks.js';

export type FeatureCommitHistoryState = {
  timelineSplit?: EnvGroup,
  head?: string,
  until?: Temporal.PlainDate,
  viewSince?: Temporal.PlainDate,
  viewUntil?: Temporal.PlainDate,
  testId?: Stats.TestId,
  testTimeline?: EnvGroup,
  selectedCommitId?: string,
}

const EXPAND_COUNT = 10;
const LOAD_COUNT = 20;
// The first load brings every tested commit of the anchor day, up to this many.
const MAX_LOAD_COUNT = 100;

type FeedElement = {
  type: 'commit',
  day: Temporal.PlainDate,
  commitId: Stats.CommitId,
  commit: WireTypes.Commit,
  stats?: WireTypes.CommitStats,
  isBeforeExpand?: boolean,
  isAfterExpand?: boolean,
} | {
  type: 'expand',
  day: Temporal.PlainDate,
  head: string,
  offset: number,
  count: number,
}

function toFeedElement(chunk: WireTypes.CommitFeedElement): FeedElement {
  return chunk.type === 'commit' ? {
    type: 'commit',
    day: Temporal.PlainDate.from(chunk.day),
    stats: chunk.stats,
    commitId: chunk.commit.commitId,
    commit: chunk.commit,
  } : {
    type: 'expand',
    day: Temporal.PlainDate.from(chunk.day),
    head: chunk.head,
    offset: chunk.offset,
    count: chunk.count,
  };
}

@customElement('feature-commit-history')
export class FeatureCommitHistory extends LitElement {

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.projectReferences, subscribe: true }) private _branches?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;

  @property({ attribute: false }) state?: FeatureCommitHistoryState;
  @property({ attribute: false }) linkifier?: Linkifier<FeatureCommitHistoryState>;

  @property({ attribute: false }) testStats?: WireTypes.TestStats;

  private _headRefTask = tasks.headRef(this, {
    head: () => this.state?.head,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
    defaultBranch: () => this._branches?.defaultBranch,
  });

  // The feature picks its own head, so it owns the default split of that head's history.
  private _defaultSplit = tasks.defaultSplit(this, {
    preset: () => this._headRefTask.value ? { type: 'commit', commitId: this._headRefTask.value.commit.commitId } : undefined,
    project: () => this._project,
    snapshotId: () => this._snapshotId,
  });

  private _split() {
    return this.state?.timelineSplit ?? this._defaultSplit.value;
  }

  private _canonicalLinkTask = new Task(this, {
    args: () => [this.state?.timelineSplit, this._defaultSplit.value] as const,
    task: async ([split, defaultSplit]) => {
      if (split && defaultSplit && split.isEqual(defaultSplit))
        handleLink(this._router, this._linkify({ timelineSplit: undefined }), { replace: true });
    },
  });

  private _anchorDayTask = new Task(this, {
    args: () => [
      this._project, this._headRefTask.value, this.state?.until?.toString(),
    ] as const,
    task: async([project, headRef, untilText,]) => {
      CHECK(headRef && project);
      return untilText ? Temporal.PlainDate.from(untilText) : Temporal.Instant.fromEpochMilliseconds(headRef.commit.timestamp).toZonedDateTimeISO(project.timezone).toPlainDate();
    }
  });

  private _testHealthCalendarLinkifier: TestHealthCalendarLinkifier = options => this._linkify({
    until: options.selectedUntil,
    selectedCommitId: undefined,
    viewSince: options.viewportSince,
    viewUntil: options.viewportUntil,
  });

  private _testHealthCalendarStateTask = new Task(this, {
    args: () => [this._anchorDayTask.value, this.state?.viewSince, this.state?.viewUntil],
    task: async([anchorDay, viewSince, viewUntil]) => {
      return createTestHealthCalendarState({
        selectedSince: anchorDay,
        selectedUntil: anchorDay,
        viewportSince: viewSince,
        viewportUntil: viewUntil,
      });
    }
  });

  /**
   * Outcomes plotted on the daily chart. Empty plots the day total instead. Only
   * meaningful for the tests-per-day metric; a selected test switches the chart
   * to durations, which have no breakdown.
   */
  @state() private _selectedOutcomes: WireTypes.Outcome[] = [];

  @state() private _feed: FeedElement[] = [];
  @state() private _anchorDaySummary?: WireTypes.PeriodSummary;
  @state() private _feedContinuation?: () => void;

  private _commitsStreamTask = new Task(this, {
    args: () => [
      this._project, this._snapshotId, this._headRefTask.value, this.state?.testId, this._split(), this.state?.testTimeline, this._anchorDayTask.value,
    ] as const,
    task: async ([project, snapshotId, headRef, testId, timelineSplit, testTimeline, anchorDay], { signal }) => {
      CHECK(project && snapshotId && headRef && timelineSplit && anchorDay);

      const split = testTimeline ?? timelineSplit;
      const anchorDaySummary = await api.history.periodSummary.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head: headRef.commit.commitId,
        timeZoneId: project.timezone,
        acceptableFlakinessRatio: project.acceptableFlakinessRatio,
        period: 'day',
        since: anchorDay.toPlainDateTime().toString(),
        testId,
        timelineSplit: split.serialize(),
      }, { signal });
      this._anchorDaySummary = anchorDaySummary;
      await this._loadFeed({
        project,
        snapshotId,
        head: headRef.commit.commitId,
        split,
        testId,
        fromDay: anchorDay,
        limit: Math.min(Math.max(LOAD_COUNT, anchorDaySummary.commits.tested), MAX_LOAD_COUNT),
      }, 'replace', signal);
    }
  });

  @state() private _isLoadingFeed = false;

  private async _loadContinuation() {
    if (!this._feedContinuation || this._isLoadingFeed)
      return;
    this._isLoadingFeed = true;
    try {
      await this._feedContinuation?.();
    } finally {
      this._isLoadingFeed = false;
    }
  }

  private async _loadFeed(options: {
    project: WireTypes.Project,
    snapshotId: WireTypes.DataSnapshotId,
    head: Stats.CommitId,
    split: EnvGroup,
    testId?: Stats.TestId,
    fromDay?: Temporal.PlainDate,
    limit?: number,
    continuation?: Stats.CommitId,
  }, mode: 'append'|'replace', signal?: AbortSignal) {
    const feed = this._feed;
    const response = await api.history.streamTestedCommits.POST({
      head: options.head,
      limit: options.limit ?? LOAD_COUNT,
      orgSlug: options.project.org.orgSlug,
      projectSlug: options.project.projectSlug,
      snapshotId: options.snapshotId,
      split: options.split.serialize(),
      testId: options.testId,
      timeZoneId: options.project.timezone,
      fromDay: options.fromDay?.toString(),
      continuation: options.continuation,
    }, { signal });
    // If the feed container has changed under our feet while we were loading
    // data, then the "load more.." request is not relevant anymore, and we
    // must discard it.
    if (feed !== this._feed)
      return;

    if (mode === 'replace')
      this._feed = [];

    this._feedContinuation = response.continuation ? this._loadFeed.bind(this, {
      ...options,
      limit: undefined,
      continuation: response.continuation,
    }, 'append') : undefined;
    this._feed.push(...response.feed.map(toFeedElement));
  }

  // These are only recomputed whenever user changes their SELECTION.
  private _selectedEnvironmentsTask = new Task(this, {
    args: () => [
      this._project,
      this._snapshotId,
      this._headRefTask.value,
      this._testHealthCalendarStateTask.value,
      this.state?.testId,
    ] as const,
    argsEqual: (
      [oldProj, oldSnapshotId, oldRef, oldCalendar, oldTestId],
      [newProj, newSnapshotId, newRef, newCalendar, newTestId],
    ) => {
      return oldProj === newProj && oldRef === newRef
        && oldSnapshotId === newSnapshotId
        && oldTestId === newTestId
        //NOTE: only selection changes
        && oldCalendar?.selectedSince.toString() === newCalendar?.selectedSince.toString()
        && oldCalendar?.selectedUntil.toString() === newCalendar?.selectedUntil.toString()
    },
    task: async ([project, snapshotId, headRef, calendar, testId], { signal }) => {
      CHECK(project && snapshotId && headRef && calendar);
      const timezone = project.timezone;
      return await api.report.allEnvironments.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        commitOptions: {
          head: headRef.commit.commitId,
          sinceTimestamp: calendar.selectedSince.toZonedDateTime(timezone).epochMilliseconds as FlakinessReport.UnixTimestampMS,
          untilTimestamp: calendar.selectedUntil.add({ days: 1 }).toZonedDateTime(timezone).epochMilliseconds as FlakinessReport.UnixTimestampMS,
        },
        testId,
      }, { signal });
    }
  });

  // These are only recomputed whenever user changes their VIEWPORT.
  private _viewportEnvironmentsTask = new Task(this, {
    args: () => [
      this._project,
      this._snapshotId,
      this._headRefTask.value,
      this._testHealthCalendarStateTask.value,
      this.state?.testId,
    ] as const,
    argsEqual: (
      [oldProj, oldSnapshotId, oldRef, oldCalendar, oldTestId],
      [newProj, newSnapshotId, newRef, newCalendar, newTestId],
    ) => {
      return oldProj === newProj && oldRef === newRef
        && oldSnapshotId === newSnapshotId
        && oldTestId === newTestId
        //NOTE: only viewport changes
        && oldCalendar?.viewportSince.toString() === newCalendar?.viewportSince.toString()
        && oldCalendar?.viewportUntil.toString() === newCalendar?.viewportUntil.toString()
    },
    task: async ([project, snapshotId, headRef, calendar, testId], { signal }) => {
      CHECK(project && snapshotId && headRef && calendar);
      const timezone = project.timezone;
      return await api.report.allEnvironments.GET({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        commitOptions: {
          head: headRef.commit.commitId,
          sinceTimestamp: calendar.viewportSince.toZonedDateTime(timezone).epochMilliseconds as FlakinessReport.UnixTimestampMS,
          untilTimestamp: calendar.viewportUntil.add({ days: 1 }).toZonedDateTime(timezone).epochMilliseconds as FlakinessReport.UnixTimestampMS,
        },
        testId,
      }, { signal });
    }
  });

  private _dailyChartArgs() {
    return [
      this._project,
      this._snapshotId,
      this._headRefTask.value,
      this._testHealthCalendarStateTask.value,
      this.state?.testId,
      this.state?.testTimeline ?? this._split(),
    ] as const;
  }

  private _dailyChartArgsEqual(
    [oldProj, oldSnapshotId, oldRef, oldCalendar, oldTestId, oldSplit]: ReturnType<FeatureCommitHistory['_dailyChartArgs']>,
    [newProj, newSnapshotId, newRef, newCalendar, newTestId, newSplit]: ReturnType<FeatureCommitHistory['_dailyChartArgs']>,
  ) {
    return oldProj === newProj && oldRef === newRef
      && oldSnapshotId === newSnapshotId
      && oldTestId === newTestId
      && oldSplit?.etag() === newSplit?.etag()
      && oldCalendar?.viewportSince.toString() === newCalendar?.viewportSince.toString()
      && oldCalendar?.viewportUntil.toString() === newCalendar?.viewportUntil.toString();
  }

  // Tests per day. Only charted when no test is selected.
  private _dailyOutcomesTask = new Task(this, {
    args: () => this._dailyChartArgs(),
    argsEqual: (oldArgs, newArgs) => this._dailyChartArgsEqual(oldArgs, newArgs),
    task: async ([project, snapshotId, headRef, calendar, testId, split], { signal }) => {
      if (testId)
        return undefined;
      CHECK(project && snapshotId && headRef && calendar && split);
      return await api.report.dailyOutcomes.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head: headRef.commit.commitId,
        timeZoneId: project.timezone,
        acceptableFlakinessRatio: project.acceptableFlakinessRatio,
        since: calendar.viewportSince.toString(),
        until: calendar.viewportUntil.toString(),
        timelineSplit: split.serialize(),
      }, { signal });
    }
  });

  // Duration per day of the selected test. Slow to load, so it does not hold up the calendar.
  private _dailyDurationsTask = new Task(this, {
    args: () => this._dailyChartArgs(),
    argsEqual: (oldArgs, newArgs) => this._dailyChartArgsEqual(oldArgs, newArgs),
    task: async ([project, snapshotId, headRef, calendar, testId, split], { signal }) => {
      if (!testId)
        return undefined;
      CHECK(project && snapshotId && headRef && calendar && split);
      return await api.report.dailyTestDurations.POST({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        snapshotId,
        head: headRef.commit.commitId,
        timeZoneId: project.timezone,
        since: calendar.viewportSince.toString(),
        until: calendar.viewportUntil.toString(),
        testId,
        timelineSplit: split.serialize(),
      }, { signal });
    }
  });

  private _allEnvironmentsTask = new Task(this, {
    args: () => [
      this._selectedEnvironmentsTask.value,
      this._viewportEnvironmentsTask.value,
    ] as const,
    task: async ([selectedEnvs, viewportEnvs,], { signal }) => {
      CHECK(selectedEnvs && viewportEnvs);
      return Array.from(new Map<Stats.EnvId, WireTypes.RunEnvironment>([selectedEnvs, viewportEnvs].flat().map(env => [env.envId, env])).values());
    }
  });

  private _runLinker = new Task(this, {
    args: () => [this._project, this._snapshotId, this._split(), this.state?.testId, this.state?.testTimeline] as const,
    task: async ([project, snapshotId, timelineSplit, testId, testTimeline], { signal }) => {
      CHECK(project);
      return (runStats: WireTypes.WellformedRun) => PageRun.url({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        reportId: runStats.runId,
        timelineSplit: testTimeline ?? timelineSplit,
        testId,
        testTimeline,
        snapshotId,
      });
    }
  });

  // Offer a swatch only for outcomes that actually occur in the visible window,
  // the same rule the chart tooltip uses for its rows. A selected outcome stays
  // offered even once it drops to zero, so panning cannot strand a selection
  // that has no way to be turned off.
  private _chartOutcomes(): WireTypes.Outcome[] {
    const days = this._dailyOutcomesTask.value ?? [];
    const selected = this._selectedOutcomes;
    return OUTCOME_ORDER.filter(outcome =>
      selected.includes(outcome) || days.some(day => (day.testStats?.[outcome] ?? 0) > 0));
  }

  // The task keeps its last value while it reloads for a new viewport, so the status matters too.
  private _isLoadingDurations() {
    return !!this.state?.testId && (this._dailyDurationsTask.status === TaskStatus.PENDING || !this._dailyDurationsTask.value);
  }

  // The explicitly selected commit, or else the one the anchor day picks.
  private _selectedCommitId() {
    return this.state?.selectedCommitId ?? this._anchorDaySummary?.outcomeCommit?.commitId;
  }

  private _linkify(update: Partial<FeatureCommitHistoryState>) {
    if (!this.state)
      return;
    return this.linkifier?.({
      ...this.state,
      ...update,
    });
  }

  render() {
    if (!this._project)
      return nothing;

    return html`
      <v-box>
        <h-box style="gap: var(--sl-spacing-2x-large);">
          <v-box style="flex: auto; align-self: end;">
            <slot name=header></slot>
            <h-box>
              <head-selector
                .head=${this._headRefTask.value}
                @fk-select=${(event: FKHeadSelectEvent) => handleLink(this._router, this._linkify({
                  head: event.head.name,
                }))}
              ></head-selector>
              <timeline-selector ?multiple=${!this.state?.testId}
                .envs=${this._allEnvironmentsTask.value}
                .split=${this.state?.testTimeline ?? this._split()}
                .defaultSplit=${this._defaultSplit.value}
                @fk-select=${(event: CustomEvent<EnvGroup>) => {
                  if (this.state?.testId) {
                    handleLink(this._router, this._linkify({
                      testTimeline: event.detail,
                    }));
                  } else {
                    handleLink(this._router, this._linkify({ timelineSplit: event.detail }));
                  }
                }}
              ></timeline-selector>
            </h-box>

            <div class=chart-header>
              <div class=chart-label>${this.state?.testId ? 'Duration per day' : 'Tests per day'}</div>
              ${this.state?.testId ? nothing : html`
                <outcome-legend
                  .outcomes=${this._chartOutcomes()}
                  .selected=${this._selectedOutcomes}
                  @fk-select=${(event: FKOutcomeSelectEvent) => this._selectedOutcomes = event.outcomes}
                ></outcome-legend>
              `}
            </div>
            ${this._isLoadingDurations() ? html`
              <h-box class=chart-loading>
                <sl-spinner></sl-spinner>
                <span>Loading durations…</span>
              </h-box>
            ` : html`<daily-metric-chart
              style="flex: auto; height: 133px;"
              .metric=${this.state?.testId ? 'durationMs' : 'testsCount'}
              .outcomes=${this._dailyOutcomesTask.value}
              .durations=${this._dailyDurationsTask.value}
              .selectedOutcomes=${this._selectedOutcomes}
              .selectedDay=${this._anchorDayTask.value}
              .viewportSince=${this._testHealthCalendarStateTask.value?.viewportSince}
              .viewportUntil=${this._testHealthCalendarStateTask.value?.viewportUntil}
              .linkifier=${(day: Temporal.PlainDate) => this._linkify({
                until: day,
                selectedCommitId: undefined,
                viewSince: this._testHealthCalendarStateTask.value?.viewportSince,
                viewUntil: this._testHealthCalendarStateTask.value?.viewportUntil,
              })}
            ></daily-metric-chart>`}
          </v-box>
          <test-health-calendar
            .hideRangeButtons=${true}
            .linkifier=${this._testHealthCalendarLinkifier}
            .split=${this.state?.testTimeline ?? this._split()}
            .head=${this._headRefTask.value}
            .state=${this._testHealthCalendarStateTask.value}
            .testId=${this.state?.testId}
          ></test-health-calendar>
        </h-box>
        ${this._renderFeedAndMinimap()}

      </v-box>
    `
  }

  private _renderFeedAndMinimap() {
    const feed = this._feed;
    
    if (!feed || !feed.length) {
      return this._commitsStreamTask.render({
        pending: () => html`
          <h1 style="color: var(--fk-color-border);">
            <h-box>
              <sl-spinner></sl-spinner>
              <span>Loading...</span>
            </h-box>
          </h1>
        `,
        complete: () => html`
          <h1 style="color: var(--fk-color-border);">No Commits</h1>
        `,
        error: () => nothing,
      });
    }
    const anchorDay = this._anchorDayTask.value ?? Temporal.Now.plainDateISO();
    const commitStats = feed.filter(chunk => chunk.type === 'commit').map(chunk => chunk.stats).filter(c => c !== undefined);
    return html`
      <v-box style="position: relative;">
        ${this._commitsStreamTask.render({
          pending: () => html`<fk-loading-glass></fk-loading-glass>`,
        })}
        <section-separator>Commits from ${anchorDay.toLocaleString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</section-separator>
        ${commitStats.length ? html`
          <commits-minimap
            .commitStats=${commitStats.toReversed()}
            .baselineType=${this.state?.testId ? 'duration' : 'testruns'}
            .selectedCommitId=${this._selectedCommitId()}
            .linkifier=${(stats?: WireTypes.CommitStats) => () => {
              handleLink(this._router, this._linkify({
                selectedCommitId: stats?.commit.commitId,
              }));
            }}
          >
            <div slot=header>${this.state?.testId ? 'Duration per commit' : 'Tests per commit'}</div>
            ${this._feedContinuation ? html`
              <sl-icon-button ?disabled=${this._isLoadingFeed} @click=${this._loadContinuation} name=plus-circle slot=prefix></sl-icon-button>
            ` : nothing}
          </commits-minimap>
        ` : nothing}
        ${this._renderFeed(feed)}
      </v-box>
    `;
  }

  private _renderFeed(feed?: FeedElement[]) {
    const project = this._project;
    const tz = this._project?.timezone;
    if (!feed || !tz || !project)
      return nothing;

    const index = feed.findIndex(chunk => chunk.type === 'commit' && chunk.commitId === this._selectedCommitId());
    const preselectedCount = index !== -1 ? feed.slice(0, index).reduce((acc, element) => 
      element.type === 'commit' ? acc + 1 : acc + element.count, 0
    ) : 0;
    if (index !== -1)
      feed = feed.slice(index);
    const selectedCommitId = index === -1 ? feed.map(e => e.type === 'commit' ? e.commitId : undefined).find(x => x !== undefined) : this._selectedCommitId();

    const baselines = new Map<Stats.CommitId, number>();
    const metricValue = this.state?.testId
      ? (s: WireTypes.CommitStats) => s.durationMs
      : (s: WireTypes.CommitStats) => TestOutcomes.sumAllCounts(s.testStats);

    let lastValue: number|undefined;
    for (const c of feed.toReversed()) {
      if (c.type !== 'commit' || !c.stats)
        continue;
      const value = metricValue(c.stats);
      if (value === undefined)
        continue;
      if (lastValue !== undefined)
        baselines.set(c.commitId, lastValue);
      lastValue = value;
    }

    const days: {
      day: Temporal.PlainDate,
      dayFeed: FeedElement[],
    }[] = [];
    for (const feedElement of feed) {
      let lastDay = days.at(-1);
      if (!lastDay || Temporal.PlainDate.compare(lastDay.day, feedElement.day) !== 0) {
        lastDay = { day: feedElement.day, dayFeed: [] };
        days.push(lastDay);
      }
      lastDay.dayFeed.push(feedElement);
    }
    for (const { dayFeed } of days) {
      for (let i = 0; i  < dayFeed.length; ++i) {
        const elem = dayFeed[i];
        if (elem.type !== 'commit')
          continue;
        elem.isAfterExpand = !!dayFeed[i - 1]?.type && dayFeed[i - 1]?.type !== 'commit';
        elem.isBeforeExpand = !!dayFeed[i + 1]?.type && dayFeed[i + 1]?.type !== 'commit';
      }
    }
    return html`
      <section class=all-days-list style="position: relative;">
        ${days.map(({ day, dayFeed }, dayIdx) => html`
          <h-box class=day-title>
            <sl-icon library=boxicons name='bx-git-commit'></sl-icon>
            <div class=day-date>Commits on <a class=day-test-link href=${PageProjectTests.url({
              orgSlug: project.org.orgSlug,
              projectSlug: project.projectSlug,
              head: this.state?.head,
              snapshotId: this._snapshotId,
              timelineSplit: this.state?.testTimeline && this.state?.testId ? this.state.testTimeline : this._split(),
              since: day,
              until: day,
              viewSince: this.state?.viewSince,
              viewUntil: this.state?.viewUntil,
            })}>${day.toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}</a></div>
            ${dayIdx === 0 ? html`
              <x-filler></x-filler>
              <span>${this.state?.testId ? 'Duration' : 'Tests'}</span>
            ` : nothing}
          </h-box>
          <div class=day-section>
            <div class=divider>
              <div class=line></div>
            </div>
            <div class=day-commits>
              ${dayIdx === 0 && this._selectedCommitId() && index > 0 ? html`
                <h-box class=${classMap({
                  'commit-stats': true,
                  'expand': true,
                })} @click=${async () => {
                  // Clearing the selection would fall back to the anchor day's commit.
                  handleLink(this._router, this._linkify({ selectedCommitId: this._feed.find(chunk => chunk.type === 'commit')?.commitId }));
                }}>
                  <span class=expansion-text>expand ${preselectedCount} commit${preselectedCount === 1 ? '' : 's'}</span>
                </h-box>
              ` : nothing}
              ${dayFeed.map(feedElement =>
                feedElement.type === 'commit'
                ? this._renderCommitStats(feedElement, selectedCommitId, baselines.get(feedElement.commitId))
                : this._renderExpand(feedElement))} 
            </div>
          </div>
        `)}
        ${this._feedContinuation ? html`
          <sl-button class=load-more-commits variant=primary ?disabled=${this._isLoadingFeed} size=large @click=${() => void this._loadContinuation()}>Show More</sl-button>
        ` : nothing}
      </section>
    `;
  }

  @state() private _pendingLoads = new Set<number>();

  private _renderExpand(feedElement: FeedElement) {
    CHECK(feedElement.type === 'expand');
    return html`
      <h-box class=${classMap({
        'commit-stats': true,
        'expand': true,
      })} @click=${async () => {
        const project = this._project;
        const snapshotId = this._snapshotId;
        if (!project || !snapshotId)
          return;
        if (this._pendingLoads.has(feedElement.offset))
          return;
        this._pendingLoads.add(feedElement.offset);
        const expanded = await api.history.expandCommitGroup.GET({
          orgSlug: project.org.orgSlug,
          projectSlug: project.projectSlug,
          snapshotId,
          head: feedElement.head,
          timeZoneId: project.timezone,
          offset: feedElement.offset,
          count: feedElement.count,
          limit: EXPAND_COUNT,
        }).finally(() => {
          this._pendingLoads.delete(feedElement.offset);
        });
        const idx = this._feed.indexOf(feedElement);
        if (idx === -1)
          return;

        this._feed.splice(idx, 1, ...expanded.map(toFeedElement));
        this.requestUpdate();
      }}>
        <span class=expansion-text>expand ${feedElement.count} commits</span>
      </h-box>
    `
  }

  // In per-test history, a commit links to the run that best characterizes the test's outcome
  // in this commit; without such a run, it falls back to the commit page filtered by the test.
  private _commitHref(commit: WireTypes.Commit, commitStats?: WireTypes.CommitStats) {
    const project = this._project;
    if (!this.testStats || !project)
      return nothing;
    if (commitStats?.representativeRunId !== undefined) {
      return PageRun.url({
        orgSlug: project.org.orgSlug,
        projectSlug: project.projectSlug,
        reportId: commitStats.representativeRunId,
        timelineSplit: this.state?.testTimeline ?? this._split(),
        testId: this.state?.testId,
        testTimeline: this.state?.testTimeline,
        snapshotId: this._snapshotId,
      });
    }
    return PageCommit.url({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      sha: commit.commitId,
      query: this.testStats.test.titles.reduce((fql, title) => fql.toggleFilter(Matcher.TEST_CONTAINS.create([title])), Query.EMPTY).serialize(),
      snapshotId: this._snapshotId,
    });
  }

  private _renderCommitStats(feedElement: FeedElement, selectedCommitId?: string, baseline?: number) {
    CHECK(feedElement.type === 'commit');
    const commitStats = feedElement.stats;
    const commit = feedElement.commit;
    const outcome = commitStats ? wireOutcomesToOutcome(commitStats.testStats, this._project?.acceptableFlakinessRatio ?? 0) : undefined;

    return html`
      <h-box class=${classMap({
        'commit-stats': true,
        'is-after-expand': !!feedElement.isAfterExpand,
        'is-before-expand': !!feedElement.isBeforeExpand,
        [outcome ?? 'untested']: true,
      })} style="gap: var(--sl-spacing-x-large);"> 

        ${commitStats && this.state?.testId ? html`
          <fk-outcome outcome=${outcome}></fk-outcome>
        ` : commitStats ? html`
          <outcomes-bar
            .counts=${commitStats.testStats}
            .linkifier=${this._project ? (o: WireTypes.Outcome) => PageCommit.url({
              orgSlug: this._project!.org.orgSlug,
              projectSlug: this._project!.projectSlug,
              sha: commit.commitId,
              query: `status:${outcomeToStatusFilter(o)}`,
              snapshotId: this._snapshotId,
            }) : undefined}
          ></outcomes-bar>
        ` : nothing}
        <fk-commit .href=${this._commitHref(commit, commitStats)} .project=${this._project} .commit=${commit}>
          <sl-icon slot=details name=dot></sl-icon>

            <runstats-selector slot=details
              .commitStats=${commitStats}
              .runlinker=${this._runLinker.value}
              .split=${(this.state?.testTimeline && this.state?.testId ? this.state.testTimeline : this._split())}
              .testId=${this.state?.testId}
            ></runstats-selector>

        </fk-commit>
        
        ${this.state?.testId ? html`
          ${commitStats?.durationMs === undefined ? nothing :
            baseline !== undefined ?
              html`<fk-duration-trend .ms=${commitStats.durationMs} .baseline=${baseline} duration></fk-duration-trend>` :
              html`<time-interval .ms=${commitStats.durationMs}></time-interval>`
          }
        ` : commitStats ? html`
          ${baseline !== undefined ?
            html`<fk-number-trend .value=${TestOutcomes.sumAllCounts(commitStats.testStats)} .baseline=${baseline}></fk-number-trend>` :
            html`<int-num .x=${TestOutcomes.sumAllCounts(commitStats.testStats)}></int-num>`
          }
        ` : nothing}
      </h-box>
    `
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .header {
      font-size: var(--sl-font-size-x-large);
    }

    * {
      box-sizing: border-box;
    }

    /** define grid structure */
    .all-days-list {
      display: grid;
      grid-template-columns: auto auto auto 1fr auto auto;

      .day-title {
        grid-column: 1/-2;
        margin-right: var(--sl-spacing-medium);
      }

      .day-section {
        grid-column: 1/-1;
        display: grid;
        grid-template-columns: subgrid;

        .divider { grid-column: 1; }

        .day-commits {
          grid-column: 2/-1;
          display: grid;
          grid-template-columns: subgrid;

          .commit-stats {
            grid-column: 1/-2;
            display: grid;
            grid-template-columns: subgrid;

            fk-commit { grid-column: 3; }
            fk-outcome { grid-column: 2; justify-self: stretch; }
            outcomes-bar { grid-column: 2; justify-self: stretch; }
            fk-duration-trend, time-duration, int-num, fk-number-trend { grid-column: 4; justify-self: end; }
          }
        }
      }
    }

    test-health-calendar {
      align-self: start;
    }

    commits-minimap {
      background: white;
      z-index: 100;
    }

    fk-duration-trend, time-duration {
      justify-self: end;
    }

    .commit-stats {
      padding: var(--sl-spacing-small) var(--sl-spacing-medium);
      border-top: 1px solid var(--fk-color-border);
      border-right: 1px solid var(--fk-color-border);
      border-left: 1px solid var(--fk-color-border);

      &:first-of-type {
        border-top-left-radius: var(--sl-border-radius-large);
        border-top-right-radius: var(--sl-border-radius-large);
      }

      &:last-of-type {
        border-bottom-left-radius: var(--sl-border-radius-large);
        border-bottom-right-radius: var(--sl-border-radius-large);
        border-bottom: 1px solid var(--fk-color-border);
      }

      &.regressed {
        background-color: color-mix(in oklch, var(--fk-color-outcome-regressed), white 90%);
      }
      &.unexpected {
        background-color: color-mix(in oklch, var(--fk-color-outcome-unexpected), white 90%);
      }
      &.skipped {
        background-color: color-mix(in oklch, var(--fk-color-outcome-skipped), white 90%);
      }
      &.flaked {
        background-color: color-mix(in oklch, var(--fk-color-outcome-flaked), white 90%);
      }
      &.expected {
        background-color: color-mix(in oklch, var(--fk-color-outcome-expected), white 90%);
      }

      &.untested {
        
      }

      &.is-after-expand {
        border-top: 1px dashed var(--fk-color-border);
      }

      &.is-before-expand {
        border-bottom: 1px dashed var(--fk-color-border);
      }
    }

    .commit-stats.expand {
      background: white;
      font-size: var(--sl-font-size-small);
      font-weight: var(--sl-font-weight-semibold);
      color: var(--sl-color-blue-400);
      padding: var(--sl-spacing-x-small);
      cursor: pointer;

      &:not(:first-of-type){
        border-top: 0;
      }

      .expansion-text {
        grid-column: 1/-1;
        justify-self: center;
      }
    }

    .day-test-link {
      text-decoration: dotted underline;
    }

    .day-title {
      color: var(--sl-color-neutral-500);
      margin: var(--sl-spacing-large) 0;
    }

    .day-commits {
    }

    sl-button.load-more-commits {
      margin-top: var(--sl-spacing-2x-large);
      grid-column: 1/-1;
    }

    .divider {
      align-self: stretch;
      width: 14px;
      display: flex;
      align-items: stretch;
      justify-content: center;
      margin-right: var(--sl-spacing-medium);

      .line {
        width: 2px;
        --spill-top: var(--sl-spacing-small);
        --spill-bottom: var(--sl-spacing-small);
        margin-top: calc(0px - var(--spill-top));
        height: calc(100% + var(--spill-top) + var(--spill-bottom));
        background: color-mix(in srgb, var(--sl-color-neutral-100) 50%, var(--sl-color-neutral-200) 50%);
      }
    }

    .day-section {
      &:last-of-type .line {
        --spill-bottom: 0;
      }
    }

    .chart-header {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: var(--sl-spacing-2x-small);
      margin-top: var(--sl-spacing-medium);
    }

    .chart-label {
      font-size: 13px;
      color: var(--sl-color-neutral-500);
    }

    /* Takes the chart's place, at the chart's size. */
    .chart-loading {
      flex: auto;
      height: 133px;
      justify-content: center;
      font-size: 13px;
      color: var(--sl-color-neutral-500);
    }
  `];
}

@customElement('feature-commit-history-popup')
export class FeatureCommitHistoryPopup extends LitElement {

  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;

  @state() private _historyTestStats?: WireTypes.TestStats;
  @state() private _historyState?: FeatureCommitHistoryState;
  @state() private _historyKey = 0;

  @query('sl-dialog') private _historyDialog?: SlDialog;

  hide() {
    this._historyDialog?.hide();
  }

  show(options: {
    testStats?: WireTypes.TestStats,
    state: FeatureCommitHistoryState 
  }) {
    this._historyState = options.state;
    this._historyTestStats = options.testStats;
    this._historyKey++;
    this._historyDialog?.show();
  }

  /**
   * Opens the test's history on a clicked history bar: on the commit behind the bar's outcome, and
   * on that commit's day.
   */
  async showHistoryBar(options: {
    testStats: WireTypes.TestStats,
    selection: TestHistorySnippetSelection,
    state: FeatureCommitHistoryState,
    // The commit the history bars were built from, if not `state.head`: the base branch in a
    // pull request, whose history must not pick up the pull request's own commits.
    historyHead?: string,
  }) {
    const { testStats, selection, state } = options;
    // A commit bar is a single commit.
    if (selection.entry.historyType === 'commit') {
      this.show({ testStats, state: { ...state, until: selection.until, selectedCommitId: selection.entry.commitId } });
      return;
    }
    const outcomeCommit = await this._periodOutcomeCommit(testStats, selection, options.historyHead ?? state.head).catch(() => undefined);
    this.show({
      testStats,
      state: {
        ...state,
        until: outcomeCommit ? Temporal.PlainDate.from(outcomeCommit.day) : selection.until,
        selectedCommitId: outcomeCommit?.commitId,
      },
    });
  }

  private async _periodOutcomeCommit(testStats: WireTypes.TestStats, selection: TestHistorySnippetSelection, head?: string) {
    const project = this._project;
    const snapshotId = this._snapshotId;
    const period = selection.entry.historyType;
    if (!project || !snapshotId || !head || period === 'commit')
      return undefined;
    const summary = await api.history.periodSummary.POST({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
      snapshotId,
      head,
      timeZoneId: project.timezone,
      acceptableFlakinessRatio: project.acceptableFlakinessRatio,
      period,
      since: Temporal.Instant.fromEpochMilliseconds(selection.entry.sinceTimestamp).toZonedDateTimeISO(project.timezone).toPlainDateTime().toString(),
      testId: testStats.test.testId,
      timelineSplit: testStats.timeline,
    });
    return summary.outcomeCommit;
  }

  render() {
    
    return html`
      <sl-dialog style="--width: 75vw;">
        ${this._historyTestStats ? html`
          <teststats-title
            slot=label
            .testStats=${this._historyTestStats}
          ></teststats-title>
        ` : html`
          <div slot=label>
            Environment History
          </div>
        `}
        ${keyed(this._historyKey, html`
          <feature-commit-history
            .state=${this._historyState}
            .testStats=${this._historyTestStats}
            .linkifier=${(e: FeatureCommitHistoryState) => () => {
              this._historyState = e;
            }}
          ></feature-commit-history>
        `)}
      </sl-dialog>
    `
  }

  static styles = [css`
    sl-dialog::part(panel) {
      height: 100vh;
    }
  `];
}

export class FKHistoryRequested extends Event {
  static readonly eventName = 'fk-history-requested';
  readonly testStats: WireTypes.TestStats;
  readonly commitId?: Stats.CommitId;

  constructor(options: {
    testStats: WireTypes.TestStats,
    commitId?: Stats.CommitId,
  }) {
    super(FKHistoryRequested.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.testStats = options.testStats;
    this.commitId = options.commitId;
  }
}
